import { css } from 'styled-components';

/**
 * 반응형 브레이크포인트 정의
 * 프로젝트 전체에서 가장 많이 사용되는 브레이크포인트를 기준으로 설정
 * - mobile: 480px (78회 사용)
 * - tablet: 768px (36회 사용)
 * - desktop: 1279px (sidebar 분기점, 1280px에서 sidebar가 보여야 하므로 1279px로 설정)
 * - filterDesktop: 1535px (필터 시스템 전용, 1536px에서 필터가 항상 고정되므로 1535px로 설정)
 */
const sizes = {
  mobile: 480,
  tablet: 768,
  laptop: 1023,
  desktop: 1279,
  filterDesktop: 1535,
} as const;

/**
 * 미디어 쿼리 헬퍼
 *
 * @example
 * ```tsx
 * const Component = styled.div`
 *   width: 100%;
 *
 *   ${media.tablet`
 *     width: 50%;
 *   `}
 *
 *   ${media.mobile`
 *     width: 100%;
 *   `}
 * `;
 * ```
 */
export const media = {
  /**
   * 모바일: 최대 480px
   */
  mobile: (...args: Parameters<typeof css>) => css`
    @media (max-width: ${sizes.mobile}px) {
      ${css(...args)}
    }
  `,

  /**
   * 태블릿: 최대 768px
   */
  tablet: (...args: Parameters<typeof css>) => css`
    @media (max-width: ${sizes.tablet}px) {
      ${css(...args)}
    }
  `,

  /**
   * 랩톱: 최대 1023px
   */
  laptop: (...args: Parameters<typeof css>) => css`
    @media (max-width: ${sizes.laptop}px) {
      ${css(...args)}
    }
  `,

  /**
   * 데스크톱: 최대 1279px
   */
  desktop: (...args: Parameters<typeof css>) => css`
    @media (max-width: ${sizes.desktop}px) {
      ${css(...args)}
    }
  `,

  /**
   * 모바일 이상: 최소 481px
   */
  minMobile: (...args: Parameters<typeof css>) => css`
    @media (min-width: ${sizes.mobile + 1}px) {
      ${css(...args)}
    }
  `,

  /**
   * 태블릿 이상: 최소 769px
   */
  minTablet: (...args: Parameters<typeof css>) => css`
    @media (min-width: ${sizes.tablet + 1}px) {
      ${css(...args)}
    }
  `,

  /**
   * 랩톱 이상: 최소 1024px
   */
  minLaptop: (...args: Parameters<typeof css>) => css`
    @media (min-width: ${sizes.laptop + 1}px) {
      ${css(...args)}
    }
  `,

  /**
   * 데스크톱 이상: 최소 1280px
   */
  minDesktop: (...args: Parameters<typeof css>) => css`
    @media (min-width: ${sizes.desktop + 1}px) {
      ${css(...args)}
    }
  `,

  /**
   * 필터 데스크톱: 최대 1535px
   */
  filterDesktop: (...args: Parameters<typeof css>) => css`
    @media (max-width: ${sizes.filterDesktop}px) {
      ${css(...args)}
    }
  `,

  /**
   * 필터 데스크톱 이상: 최소 1536px
   */
  minFilterDesktop: (...args: Parameters<typeof css>) => css`
    @media (min-width: ${sizes.filterDesktop + 1}px) {
      ${css(...args)}
    }
  `,
} as const;

/**
 * 브레이크포인트 값 export (필요한 경우 직접 사용)
 */
export { sizes as breakpoints };
